/**
 * Platform Response Mapper
 * Validates platform stats & health responses and maps to domain types
 */
import { z } from 'zod'
import type { PlatformStats, PlatformHealth, ServiceHealthEntry, HealthStatus } from '@domain/entities'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import { normalizeToCamelCase } from '@shared/utils/caseNormalize'

const logger = createLoggerWithPrefix('platformMapper')

// --- Stats ---

const platformStatsDataSchema = z.object({
  totalTenants: z.number(),
  activeTenants: z.number(),
  totalUsers: z.number(),
  totalWorkflows: z.number().nullable().default(null),
  totalExecutions: z.number().nullable().default(null),
})

const platformStatsResponseSchema = z.object({
  success: z.boolean(),
  data: platformStatsDataSchema,
})

export const mapPlatformStatsResponse = (response: unknown): PlatformStats | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Stats response is null or not an object')
    return null
  }

  // Normalize PascalCase keys from .NET backend to camelCase
  const normalized = normalizeToCamelCase(response)

  // Try wrapped { success, data } envelope
  const wrapperResult = platformStatsResponseSchema.safeParse(normalized)
  if (wrapperResult.success) {
    return wrapperResult.data.data
  }

  // Fallback: try extracting data field or direct object
  const normalizedObj = normalized as Record<string, unknown>
  const rawData = normalizedObj.data ?? normalized
  const dataResult = platformStatsDataSchema.safeParse(rawData)
  if (dataResult.success) {
    return dataResult.data
  }

  logger.error('Stats validation failed:', dataResult.error.format())
  return null
}

// --- Health ---

const healthStatusSchema = z.enum(['Healthy', 'Degraded', 'Unhealthy'])

const serviceHealthEntrySchema = z.object({
  name: z.string(),
  status: healthStatusSchema,
  latencyMs: z.number().nullable().default(null),
  lastChecked: z.string(),
})

const platformHealthDataSchema = z.object({
  overallStatus: healthStatusSchema,
  services: z.array(serviceHealthEntrySchema),
  timestamp: z.string(),
})

// Backend may return details as a dictionary instead of services array
const platformHealthDetailsSchema = z.object({
  overallStatus: healthStatusSchema,
  details: z.record(z.object({
    status: healthStatusSchema,
    latencyMs: z.number().nullable().optional(),
    lastChecked: z.string().optional(),
  })),
  timestamp: z.string(),
})

const platformHealthResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([platformHealthDataSchema, platformHealthDetailsSchema]),
})

// USM backend returns: { status: "Healthy", databaseConnected: bool, timestamp: "...", details: { "Database": "Connected" } }
// where details values are plain strings, not objects
const usmHealthSchema = z.object({
  status: z.string(),
  databaseConnected: z.boolean().optional(),
  timestamp: z.string(),
  details: z.record(z.string()).optional(),
})

const mapStringToHealthStatus = (str: string): HealthStatus => {
  const lower = str.toLowerCase()
  if (lower === 'healthy' || lower === 'connected' || lower === 'ok') return 'Healthy'
  if (lower === 'degraded') return 'Degraded'
  return 'Unhealthy'
}

const normalizeHealthData = (data: unknown): PlatformHealth | null => {
  // Try direct services array format
  const directResult = platformHealthDataSchema.safeParse(data)
  if (directResult.success) {
    return directResult.data
  }

  // Try details dictionary format with object values → convert to services array
  const detailsResult = platformHealthDetailsSchema.safeParse(data)
  if (detailsResult.success) {
    const { overallStatus, details, timestamp } = detailsResult.data
    const services: ServiceHealthEntry[] = Object.entries(details).map(([name, detail]) => ({
      name,
      status: detail.status,
      latencyMs: detail.latencyMs ?? null,
      lastChecked: detail.lastChecked ?? timestamp,
    }))
    return { overallStatus, services, timestamp }
  }

  // Try USM backend format: { status, databaseConnected, timestamp, details: { key: string } }
  const usmResult = usmHealthSchema.safeParse(data)
  if (usmResult.success) {
    const { status, details, timestamp } = usmResult.data
    const services: ServiceHealthEntry[] = Object.entries(details ?? {}).map(([name, value]) => ({
      name,
      status: mapStringToHealthStatus(value),
      latencyMs: null,
      lastChecked: timestamp,
    }))
    return {
      overallStatus: mapStringToHealthStatus(status),
      services,
      timestamp,
    }
  }

  return null
}

export const mapPlatformHealthResponse = (response: unknown): PlatformHealth | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Health response is null or not an object')
    return null
  }

  // Normalize PascalCase keys from .NET backend to camelCase
  const normalized = normalizeToCamelCase(response)

  // Try wrapped envelope
  const wrapperResult = platformHealthResponseSchema.safeParse(normalized)
  if (wrapperResult.success) {
    return normalizeHealthData(wrapperResult.data.data)
  }

  // Fallback: try extracting data field or direct object
  const normalizedObj = normalized as Record<string, unknown>
  const rawData = normalizedObj.data ?? normalized
  const result = normalizeHealthData(rawData)
  if (result) return result

  logger.error('Health validation failed')
  return null
}

// --- Gateway Health ---

const gatewayHealthSchema = z.object({
  status: z.string(),
  service: z.string().optional(),
})

export interface GatewayHealthResult {
  status: string
  isHealthy: boolean
}

export const mapGatewayHealthResponse = (response: unknown): GatewayHealthResult | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Gateway health response is null or not an object')
    return null
  }

  const result = gatewayHealthSchema.safeParse(response)
  if (result.success) {
    const status = result.data.status.toLowerCase()
    return {
      status: result.data.status,
      isHealthy: status === 'healthy' || status === 'ok',
    }
  }

  // Any 200 response means gateway is reachable
  return { status: 'Reachable', isHealthy: true }
}
